import { ExpandedGuest } from "../form/guestTab";
import { GuestStatus } from "../types";
import { apiSlice } from "./api";
import { ventsApi } from "./ventsApi";

export interface GetGuestsRequest {
  ventId: string;
}
export interface GetGuestsResponseRecord {
  id: string;
  ventId: string;
  firstName: string;
  lastName?: string;
  primaryGuestId?: string;
  primaryFirstName?: string;
  primaryLastName?: string;
  updateTime: string;
  status: GuestStatus;
  canUserEdit: boolean;
  canUserDelete: boolean;
  canUserSendMessage: boolean; // true if user can send a message to this guest
  email: string;
  messages?: string;
  customFieldValues?: Record<string, string[]>;
  hostNote?: string; // Note from host about this guest
  familyGreeting?: string;
}
export interface GetGuestsResponse {
  guests: GetGuestsResponseRecord[];
}

export interface SaveGuestResponse {
  id: string;
  updateTime: string; // ISO 8601
}
export interface SaveGuestRequest {
  id: string;
  ventId: string; // not used by server, but helps client update cached query results for getting all guests
  primaryGuestId?: string;
  email?: string;
  firstName: string;
  lastName?: string;
  updateTime: string;
  status: GuestStatus;
  isUnsubscribed: boolean;
  blockedGuestMessaging: boolean;
  customFieldValues: Record<string, string[]>;
  hostNote: string; // Note from host about this guest
  familyGreeting: string;
}

export interface ConnectGuestResponse {
  guestId: string;
  newGuestInfo: {
    email: string;
    firstName: string;
    lastName: string;
  };
  updateTime: string; // ISO 8601
}
export interface ConnectGuestRequest {
  ventId: string;
  status: GuestStatus; // primary guest's status
  message: string; // message to host
  plusOnes: Record<string, GuestStatus>; // key is guestId of the plus one
}

export interface DeleteGuestResponse {
  plusOneIds: string[]; // needed to update cache
  primaryGuestId?: string; // needed to update cache
}
export interface DeleteGuestRequest {
  id: string;
  ventId: string; // Needed to easily update cache
}

export interface CreateGuestRequest {
  ventId: string;
  email?: string;
  firstName: string;
  lastName?: string;
  primaryGuestId?: string; // If supplied, this person is a plus one
  primaryGuestFirstName?: string; // helps with caching -- not needed by server
  primaryGuestLastName?: string; // helps with caching -- not needed by server
  status: GuestStatus;
}
export interface CreateGuestResponse {
  id: string;
  updateTime: string; // ISO 8601
}

export interface UploadGuestsRequest {
  ventId: string;
  file: File;
}
interface UploadGuestsResponseRecord {
  guestId: string;
  email?: string;
  firstName: string;
  lastName?: string;
  primaryGuestId?: string;
  status: GuestStatus;
  updateTime: string; // ISO 8601
}
// Return created guests to client to update cache
export interface UploadGuestsResponse {
  guests: UploadGuestsResponseRecord[];
  duplicateEmails: string[];
}

export interface GetGuestRequest {
  id: string;
}
export interface GetGuestResponse extends ExpandedGuest {}

export interface GetGuestNameRequest {
  id: string;
}
export interface GetGuestNameResponse {
  firstName: string;
  lastName?: string;
}

export interface GetUsersGuestIdForVentRequest {
  ventId: string;
}
export interface GetUsersGuestIdForVentResponse {
  guestId: string;
}

export interface SendGuestMessageResponse {}
export interface SendGuestMessageRequest {
  guestId: string;
  message: string;
}

export interface SendHostMessageResponse {
  updateTime: string;
  guestId: string;
  messages: string;
}
export interface SendHostMessageRequest {
  ventId: string;
  message: string;
}

export interface GetGuestInfoByGuidRequest {
  guestGuid: string;
}
export interface GetGuestInfoByGuidResponse {
  guestId: string;
  ventId: string;
  ventName: string; // Name of the vent, used for display
  email: string;
  firstName: string;
  lastName: string;
}

export interface GetGuestsForRsvpRequest {
  ventId: string;
}
export interface GetGuestsForRsvpResponse {
  primaryGuestId: string; // ID of primary guest -- if blank then guest has not connected yet
  primaryGuestName: string; // name of primary guest
  primaryGuestStatus: GuestStatus; // status of primary guest
  // the Record key is the guestId of the plus one
  plusOnes: Record<string, { name: string; status: GuestStatus }>;
}

export const guestApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGuest: builder.query<GetGuestResponse, GetGuestRequest>({
      query: (body) => ({
        url: "getGuest",
        body: JSON.stringify(body),
      }),
      providesTags: ["Guest"],
    }),

    getGuestName: builder.query<GetGuestNameResponse, GetGuestNameRequest>({
      query: (body) => ({
        url: "getGuestName",
        body: JSON.stringify(body),
      }),
    }),

    getUsersGuestIdForVent: builder.query<
      GetUsersGuestIdForVentResponse,
      GetUsersGuestIdForVentRequest
    >({
      query: (body) => ({
        url: "getUsersGuestIdForVent",
        body: JSON.stringify(body),
      }),
    }),

    getGuestInfoByGuid: builder.query<
      GetGuestInfoByGuidResponse,
      GetGuestInfoByGuidRequest
    >({
      query: (body) => ({
        url: "getGuestInfoByGuid",
        body: JSON.stringify(body),
      }),
    }),

    // getGuests gets all guests for an event
    getGuests: builder.query<GetGuestsResponse, GetGuestsRequest>({
      query: (body) => ({
        url: "getGuests",
        body: JSON.stringify(body),
      }),
      providesTags: ["Guests"],
    }),

    // getGuestsForRsvp gets all guest and plus ones for RSVPing
    // It is only called when a guest is not yet connected to the event,
    // so it should normally be called only at most once per guest.
    // Therefore, we don't need to cache the result.
    // This allows us to avoid updating the cached results.
    getGuestsForRsvp: builder.query<
      GetGuestsForRsvpResponse,
      GetGuestsForRsvpRequest
    >({
      query: (body) => ({
        url: "getGuestsForRsvp",
        body: JSON.stringify(body),
      }),
      keepUnusedDataFor: 0, // Disable caching - results expire immediately
    }),

    // connectGuest creates a guest if one doesn't already exist
    connectGuest: builder.mutation<ConnectGuestResponse, ConnectGuestRequest>({
      query: (body) => ({
        url: "connectGuest",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["Connect"],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getGuests",
                { ventId: request.ventId },
                (draft) => {
                  if (
                    // Server always sends non-null newGuestInfo :(
                    data.newGuestInfo == null ||
                    data.newGuestInfo.email == ""
                  ) {
                    // guest document was updated
                    // find the guest and update it
                    for (let i = 0; i < draft.guests.length; i++) {
                      if (draft.guests[i].id == data.guestId) {
                        draft.guests[i].updateTime = data.updateTime;
                        draft.guests[i].status = request.status;
                        break;
                      }
                    }
                  } else {
                    // guest document was added
                    if (draft.guests == null) {
                      draft.guests = [];
                    }
                    draft.guests.push({
                      id: data.guestId,
                      ventId: request.ventId,
                      firstName: data.newGuestInfo.firstName,
                      lastName: data.newGuestInfo.lastName,
                      status: request.status,
                      canUserEdit: true,
                      canUserDelete: true,
                      canUserSendMessage: false, // cannot send message to self
                      updateTime: data.updateTime,
                      email: data.newGuestInfo.email,
                    });
                  }
                }
              )
            )
          );

          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getGuest",
                { id: data.guestId },
                (draft) => {
                  if (
                    data.newGuestInfo == null ||
                    data.newGuestInfo.email == ""
                  ) {
                    // guest document was updated
                    draft.status = request.status;
                    draft.updateTime = data.updateTime;
                  }
                }
              )
            )
          );

          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getUsersGuestIdForVent",
                { ventId: request.ventId },
                (draft) => {
                  draft.guestId = data.guestId;
                }
              )
            )
          );

          //
        } catch (e) {
          console.log(e);
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    // createGuest creates a minimal guest
    createGuest: builder.mutation<CreateGuestResponse, CreateGuestRequest>({
      query: (body) => ({
        url: "createGuest",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;
          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getGuests",
                { ventId: request.ventId },
                (draft) => {
                  if (draft.guests == null) {
                    draft.guests = [];
                  }
                  draft.guests.push({
                    id: data.id,
                    ventId: request.ventId,
                    firstName: request.firstName,
                    lastName: request.lastName,
                    primaryFirstName: request.primaryGuestFirstName,
                    primaryLastName: request.primaryGuestLastName,
                    primaryGuestId: request.primaryGuestId,
                    status: request.status,
                    canUserEdit: true,
                    canUserDelete: true,
                    canUserSendMessage: true, // may be incorrect!
                    updateTime: data.updateTime,
                    email: request.email ?? "",
                  });
                }
              )
            )
          );

          undos.push(
            dispatch(
              // Happens when a plus one is added.
              guestApi.util.updateQueryData(
                "getGuest",
                { id: request.primaryGuestId! },
                (draft) => {
                  if (request.primaryGuestId == null) return;
                  // In case it's the first plus one
                  if (draft.plusOnes == null) {
                    draft.plusOnes = [];
                  }
                  draft.plusOnes.push({
                    id: data.id,
                    firstName: request.firstName,
                    lastName: request.lastName,
                    email: request.email,
                    status: request.status,
                  });
                }
              )
            )
          );
        } catch (e) {
          console.log(e);
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    uploadGuests: builder.mutation<UploadGuestsResponse, UploadGuestsRequest>({
      query: (request) => {
        const formData = new FormData();
        formData.append("file", request.file as Blob, request.file.name);
        formData.append("ventId", request.ventId);
        return {
          url: "uploadGuests",
          body: formData,
          // Important: Set Content-Type to undefined so that the browser
          // automatically sets it to multipart/form-data with the correct boundary.
          headers: {},
        };
      },
      invalidatesTags: ["Guests", "Guest"],
    }),

    saveGuest: builder.mutation<SaveGuestResponse, SaveGuestRequest>({
      query: (body) => {
        return {
          url: "saveGuestConfig",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            guestApi.util.updateQueryData(
              "getGuests",
              { ventId: request.ventId },
              (draft) => {
                const updatedGuests = draft.guests.map((record) => {
                  if (record.id === data.id) {
                    return {
                      id: data.id,
                      ventId: request.ventId,
                      firstName: request.firstName,
                      lastName: request.lastName,
                      email: request.email ?? "",
                      primaryGuestId: request.primaryGuestId,
                      status: request.status,
                      messages: record.messages,
                      canUserEdit: record.canUserEdit,
                      canUserDelete: record.canUserDelete,
                      canUserSendMessage: record.canUserSendMessage,
                      customFieldValues: request.customFieldValues,
                      hostNote: request.hostNote,
                      familyGreeting: request.familyGreeting,
                      updateTime: data.updateTime,
                    };
                  } else return record;
                });
                draft.guests = updatedGuests;
              }
            )
          );
          dispatch(
            guestApi.util.updateQueryData(
              "getGuest",
              { id: data.id },
              (draft) => {
                draft.firstName = request.firstName;
                draft.lastName = request.lastName;
                draft.email = request.email;
                draft.primaryGuestId = request.primaryGuestId;
                draft.updateTime = data.updateTime;
                draft.status = request.status;
                draft.isUnsubscribed = request.isUnsubscribed;
                draft.blockedGuestMessaging = request.blockedGuestMessaging;
                draft.customFieldValues = request.customFieldValues;
                draft.hostNote = request.hostNote;
                draft.familyGreeting = request.familyGreeting;
              }
            )
          );
          dispatch(
            guestApi.util.updateQueryData(
              "getGuest",
              { id: request.primaryGuestId! },
              (draft) => {
                if (request.primaryGuestId == null) return;
                for (let i = 0; i < draft.plusOnes.length; i++) {
                  if (draft.plusOnes[i].id === request.id) {
                    draft.plusOnes[i].email = request.email;
                    draft.plusOnes[i].firstName = request.firstName;
                    draft.plusOnes[i].lastName = request.lastName;
                    draft.plusOnes[i].status = request.status;
                    return;
                  }
                }
              }
            )
          );
        } catch {}
      },
    }),

    deleteGuest: builder.mutation<DeleteGuestResponse, DeleteGuestRequest>({
      query: (body) => ({
        url: "deleteGuest",
        body: JSON.stringify(body),
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const undos: any[] = []; // PatchCollection is not exposed
        try {
          const { data } = await queryFulfilled;

          // If a user is deleting their own guest record for an event, then we need to update their events as guest
          undos.push(
            dispatch(
              ventsApi.util.updateQueryData(
                "getVentsForUserAsGuest",
                {},
                (draft) => {
                  if (draft.vents == null) return;
                  for (let i = draft.vents.length - 1; i >= 0; i--) {
                    if (draft.vents[i].userGuestId === request.id) {
                      draft.vents.splice(i, 1);
                    }
                  }
                }
              )
            )
          );

          const guestIdsToDelete = [request.id];
          if (data && data.plusOneIds) {
            guestIdsToDelete.push(...data.plusOneIds);
          }
          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getGuests",
                { ventId: request.ventId },
                (draft) => {
                  if (draft.guests == null) return;
                  for (let i = draft.guests.length - 1; i >= 0; i--) {
                    if (guestIdsToDelete.includes(draft.guests[i].id)) {
                      draft.guests.splice(i, 1);
                    }
                  }
                }
              )
            )
          );

          // Remove all of these plus ones from the getGuest cache
          // There are multiple guestIdsToDelete only if we deleted a primary guest that had plus ones
          for (const id of guestIdsToDelete) {
            undos.push(
              dispatch(
                guestApi.util.updateQueryData("getGuest", { id }, () => {
                  return undefined;
                })
              )
            );
          }

          // If the guest being deleted is a plus one, then we need to remove
          // that plus one from primary guest's getGuest cache
          undos.push(
            dispatch(
              guestApi.util.updateQueryData(
                "getGuest",
                { id: data.primaryGuestId! },
                (draft) => {
                  if (draft.plusOnes == null) return;
                  for (let i = draft.plusOnes.length - 1; i >= 0; i--) {
                    if (request.id == draft.plusOnes[i].id) {
                      draft.plusOnes.splice(i, 1);
                    }
                  }
                }
              )
            )
          );
        } catch (e) {
          console.log(e);
          for (const u of undos) {
            (u as any).undo();
          }
        }
      },
    }),

    sendGuestMessage: builder.mutation<
      SendGuestMessageResponse,
      SendGuestMessageRequest
    >({
      query: (body) => ({
        url: "sendGuestMessage",
        body: JSON.stringify(body),
      }),
    }),

    sendHostMessage: builder.mutation<
      SendHostMessageResponse,
      SendHostMessageRequest
    >({
      query: (body) => {
        return {
          url: "sendHostMessage",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            guestApi.util.updateQueryData(
              "getGuests",
              { ventId: request.ventId },
              (draft) => {
                const updatedGuests = draft.guests.map((record) => {
                  if (record.id === data.guestId) {
                    const newRecord = { ...record };
                    newRecord.messages =
                      request.message + "\n" + (record.messages ?? "");
                    newRecord.updateTime = data.updateTime;
                    return newRecord;
                  } else return record;
                });
                draft.guests = updatedGuests;
              }
            )
          );
          dispatch(
            guestApi.util.updateQueryData(
              "getGuest",
              { id: data.guestId },
              (draft) => {
                draft.messages = data.messages;
                draft.updateTime = data.updateTime;
              }
            )
          );
        } catch {}
      },
    }),

    //
  }),
});

export const {
  useGetGuestQuery,
  useCreateGuestMutation,
  useGetGuestsQuery,
  useSaveGuestMutation,
  useDeleteGuestMutation,
  useConnectGuestMutation,
  useUploadGuestsMutation,
  useGetUsersGuestIdForVentQuery,
  useSendGuestMessageMutation,
  useGetGuestNameQuery,
  useSendHostMessageMutation,
  useGetGuestInfoByGuidQuery,
  useGetGuestsForRsvpQuery,
} = guestApi;
